import { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";
import { OrderItem } from "../models/IOrders";
import { getOrderHistory } from "../services/orderHistory";
import { getRestaurantById } from "../services/restaurants";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { useLanguage } from "../context/LanguageContext";
import { useLiveChat } from "../modules/LiveChat";
import FeedbackModal from "../popupPages/FeedbackModal";
import DatePicker, { toLocalIsoDate } from "../components/DatePicker";
import axios from "axios";

// Backend: StatusConstants (MutfacikApiSS.Shared/Constants/SystemConstants.cs)
const ORDER_STATUS = {
  received: "21238beb-3a97-420d-8fad-626166db1b1e",
  gettingReady: "700924e1-a720-4dbe-8374-6ae56adc4346",
  onItsWay: "840e3442-8a36-4404-8e4b-f6d1fafbbf3b",
  completed: "55b08d65-d81a-4712-8332-8e59e8340940",
  cancelled: "950565f2-baa3-4977-bef8-8e1dc5962123",
};

type StatusKind = "received" | "gettingReady" | "onItsWay" | "completed" | "cancelled";

/** Durum id'si eşleşmezse (yeni bir durum eklenirse) ada bakarak tahmin eder. */
const statusKind = (order: OrderItem): StatusKind => {
  const id = order.orderStatusId?.toLowerCase();
  const found = (Object.keys(ORDER_STATUS) as StatusKind[]).find((k) => ORDER_STATUS[k] === id);
  if (found) return found;
  const name = order.orderStatus?.orderStatusNameTr ?? "";
  if (name.includes("İptal")) return "cancelled";
  if (name.includes("Teslim")) return "completed";
  if (name.includes("Yolda")) return "onItsWay";
  if (name.includes("Hazır")) return "gettingReady";
  return "received";
};

const STATUS_ICON: Record<StatusKind, string> = {
  received: "bi-receipt",
  gettingReady: "bi-fire",
  onItsWay: "bi-bicycle",
  completed: "bi-check-circle-fill",
  cancelled: "bi-x-circle-fill",
};

const toApiDate = (isoDate: string) => {
  const [y, m, d] = isoDate.split("-");
  return `${m}.${d}.${y}`;
};

interface RestaurantInfo {
  name: string;
  logoPath?: string;
}

function OrderHistory() {
  const yesterday = toLocalIsoDate(new Date(Date.now() - 864e5));
  const tomorrow = toLocalIsoDate(new Date(Date.now() + 864e5));

  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(tomorrow);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<Record<string, RestaurantInfo>>({});
  const [feedbackOrder, setFeedbackOrder] = useState<OrderItem | null>(null);
  const feedbackTriggerRef = useRef<HTMLButtonElement>(null);
  const { t, lang } = useLanguage();
  const { openChat } = useLiveChat();
  const locale = lang === "tr" ? "tr-TR" : "en-US";

  const restaurantIdOf = (order: OrderItem) => order.preOrder?.restourantId ?? order.restourantId;

  const fetchRestaurants = (orderList: OrderItem[]) => {
    const uniqueIds = Array.from(new Set(
      orderList.map(restaurantIdOf).filter((id): id is string => !!id)
    ));
    if (uniqueIds.length === 0) return;
    Promise.all(uniqueIds.map((id) =>
      getRestaurantById(id)
        .then((res) => ({ id, info: { name: res.data.restourantName, logoPath: res.data.restourantLogoPath } }))
        .catch(() => null)
    )).then((results) => {
      const map: Record<string, RestaurantInfo> = {};
      results.forEach((r) => { if (r) map[r.id] = r.info; });
      setRestaurants(map);
    });
  };

  const fetchOrders = (start: string, end: string) => {
    setLoading(true);
    setError("");
    getOrderHistory(toApiDate(start), toApiDate(end))
      .then((res) => {
        setOrders(res.data);
        fetchRestaurants(res.data);
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setOrders([]);
        } else {
          setError(t("orderHistory.loadError"));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders(startDate, endDate);
  }, []);

  const handleSearch = () => fetchOrders(startDate, endDate);

  const handleOpenFeedback = (order: OrderItem) => {
    setFeedbackOrder(order);
    feedbackTriggerRef.current?.click();
  };

  const handleFeedbackSuccess = (orderId: string) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, isFeedbackAvailable: false } : o))
    );
  };

  const handleStartChange = (val: string) => {
    setStartDate(val);
    if (val > endDate) setEndDate(val);
  };

  const handleEndChange = (val: string) => {
    setEndDate(val);
    if (val < startDate) setStartDate(val);
  };

  const openTracking = () => {
    const el = document.getElementById("orderTrackingModal");
    if (el) Modal.getOrCreateInstance(el).show();
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    const date = d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    return `${date} • ${time}`;
  };

  const formatPrice = (amount: number, order: OrderItem) =>
    `${amount.toFixed(2)} ${order.currency?.currencyDisplayText ?? "TL"}`;

  const totalAmount = orders.reduce((s, o) => s + o.orderPrice, 0);

  return (
    <div className="container py-4">
      <h5 className="fw-bold mb-4">
        <i className="bi bi-receipt me-2"></i>{t("orderHistory.title")}
      </h5>

      {/* Hidden feedback modal trigger */}
      <button
        ref={feedbackTriggerRef}
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#feedbackModal"
      />

      {/* Tarih filtresi */}
      <div className="card shadow-sm mb-4 border-0 rounded-4">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small fw-semibold mb-1">
                {t("orderHistory.startDate")}
              </label>
              <DatePicker value={startDate} max={endDate} onChange={handleStartChange} />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label small fw-semibold mb-1">
                {t("orderHistory.endDate")}
              </label>
              <DatePicker value={endDate} min={startDate} onChange={handleEndChange} />
            </div>
            <div className="col-12 col-md-4">
              <button
                className="btn btn-danger w-100 rounded-3"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    {t("orderHistory.searching")}
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-1"></i>{t("orderHistory.search")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger small">{error}</div>
      )}

      {loading && (
        <div className="d-flex flex-column gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="oh-card oh-card--skeleton">
              <div className="skeleton oh-logo" />
              <div className="flex-grow-1 d-flex flex-column gap-2">
                <div className="skeleton" style={{ height: "16px", width: "40%" }} />
                <div className="skeleton" style={{ height: "12px", width: "65%" }} />
                <div className="skeleton" style={{ height: "12px", width: "25%" }} />
              </div>
              <div className="skeleton d-none d-md-block" style={{ height: "28px", width: "110px", borderRadius: "20px" }} />
            </div>
          ))}
        </div>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="text-center text-muted mt-5">
          <i className="bi bi-bag-x" style={{ fontSize: "3rem" }}></i>
          <p className="mt-2">{t("orderHistory.noOrders")}</p>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-3 px-1">
          <span className="text-muted small">{t("orderHistory.orderCount", { count: orders.length })}</span>
          <span className="fw-bold">
            {t("orderHistory.total")}{" "}
            <span className="text-danger">{formatPrice(totalAmount, orders[0])}</span>
          </span>
        </div>
      )}

      <FeedbackModal
        order={feedbackOrder}
        restaurantName={(() => {
          const rid = feedbackOrder ? restaurantIdOf(feedbackOrder) : undefined;
          return rid ? (restaurants[rid]?.name ?? "") : "";
        })()}
        onSuccess={handleFeedbackSuccess}
      />

      {!loading && (
        <div className="d-flex flex-column gap-3">
          {orders.map((order) => {
            const isOpen = openId === order.id;
            const toggle = () => setOpenId(isOpen ? null : order.id);
            const kind = statusKind(order);
            const isActive = kind === "received" || kind === "gettingReady" || kind === "onItsWay";
            const statusName = (lang === "en" ? order.orderStatus?.orderStatusNameEn : null)
              || order.orderStatus?.orderStatusNameTr || "";
            const foods = order.preOrder?.foodPreOrder ?? [];
            const rid = restaurantIdOf(order);
            const restaurant = rid ? restaurants[rid] : undefined;
            const firstFoodImage = foods.find((fp) => fp.food?.foodImagePath)?.food?.foodImagePath;
            const logoSrc = restaurant?.logoPath
              ? S3_IMAGE_BASE_URL + restaurant.logoPath
              : firstFoodImage ? S3_IMAGE_BASE_URL + firstFoodImage : null;
            const itemsSummary = foods
              .map((fp) => `${fp.foodQuantity > 1 ? `${fp.foodQuantity}× ` : ""}${fp.food?.foodNameTr ?? ""}`)
              .filter((s) => s.trim())
              .join(", ");

            return (
              <div key={order.id} className={`oh-card-wrap${isOpen ? " oh-card-wrap--open" : ""}`}>
                <div className="oh-card">
                  {/* Logo */}
                  <div className="oh-logo">
                    {logoSrc ? (
                      <img src={logoSrc} alt={restaurant?.name ?? ""} />
                    ) : (
                      <i className="bi bi-shop"></i>
                    )}
                  </div>

                  {/* Restoran + ürünler */}
                  <div className="oh-main">
                    <div className="oh-restaurant">{restaurant?.name ?? "…"}</div>
                    <div className="oh-items">{itemsSummary}</div>
                    <button type="button" className="oh-details-link" onClick={toggle} aria-expanded={isOpen}>
                      {isOpen ? t("orderHistory.hideDetails") : t("orderHistory.viewDetails")}
                      <i className={`bi bi-chevron-down ms-1 oh-rotate${isOpen ? " oh-rotate--open" : ""}`}></i>
                    </button>
                  </div>

                  {/* Tarih, no, tutar */}
                  <div className="oh-meta">
                    <div className="oh-date">{formatDateTime(order.orderDate)}</div>
                    <div className="oh-no">{t("orderHistory.orderNo")}: {order.orderNameUniqe}</div>
                    <div className="oh-price">{formatPrice(order.orderPrice, order)}</div>
                  </div>

                  {/* Durum + aksiyon */}
                  <div className="oh-side">
                    <span className={`oh-status oh-status--${kind}`}>
                      <i className={`bi ${STATUS_ICON[kind]}`}></i>
                      {statusName}
                    </span>
                    {isActive ? (
                      <button type="button" className="oh-action" onClick={openTracking}>
                        <i className="bi bi-geo-alt"></i>{t("orderHistory.trackOrder")}
                      </button>
                    ) : order.isFeedbackAvailable ? (
                      <button type="button" className="oh-action" onClick={() => handleOpenFeedback(order)}>
                        <i className="bi bi-star"></i>{t("orderHistory.rateOrder")}
                      </button>
                    ) : (
                      <button type="button" className="oh-action" onClick={toggle}>
                        <i className="bi bi-info-circle"></i>{t("orderHistory.orderDetail")}
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className="oh-chevron"
                    onClick={toggle}
                    aria-label={isOpen ? t("orderHistory.hideDetails") : t("orderHistory.viewDetails")}
                  >
                    <i className={`bi bi-chevron-right oh-rotate-90${isOpen ? " oh-rotate-90--open" : ""}`}></i>
                  </button>
                </div>

                {isOpen && (
                  <div className="oh-details">
                    <div className="mb-2 small">
                      {kind === "cancelled" ? (
                        <span className="text-danger fw-semibold">
                          <i className="bi bi-x-octagon-fill me-1"></i>
                          {t("orderHistory.orderCancelled")}
                        </span>
                      ) : (
                        <span className={order.isRestourantAccepted ? "text-success" : "text-muted"}>
                          <i className={`bi ${order.isRestourantAccepted ? "bi-check-circle-fill" : "bi-clock"} me-1`}></i>
                          {order.isRestourantAccepted
                            ? t("orderHistory.restaurantAccepted")
                            : t("orderHistory.restaurantPending")}
                        </span>
                      )}
                    </div>

                    {order.cancellationReason && (
                      <div className="rounded-3 px-3 py-2 mb-2 small" style={{ background: "#fff4f4", border: "1px solid #f5c6c6" }}>
                        <i className="bi bi-x-circle-fill text-danger me-1"></i>
                        <span className="fw-semibold text-danger">{t("orderHistory.cancellationReason")}: </span>
                        <span className="text-muted">{order.cancellationReason}</span>
                      </div>
                    )}

                    <div className="d-flex flex-column gap-2 mb-3">
                      {foods.map((fp) => (
                        <div key={fp.id} className="d-flex align-items-center gap-3 bg-light rounded-3 p-2">
                          {fp.food?.foodImagePath && (
                            <img
                              src={S3_IMAGE_BASE_URL + fp.food.foodImagePath}
                              alt={fp.food.foodNameTr}
                              className="rounded-3"
                              style={{ width: "56px", height: "56px", objectFit: "cover", flexShrink: 0 }}
                            />
                          )}
                          <div className="flex-grow-1">
                            <div className="fw-semibold small">{fp.food?.foodNameTr}</div>
                            <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                              {t("orderHistory.quantity", { qty: fp.foodQuantity })}
                              {(() => {
                                const unit = fp.discountedUnitPrice ?? fp.unitPrice ?? fp.food?.foodPrice;
                                if (!unit) return "";
                                const cur = order.currency?.currencyDisplayText ?? "TL";
                                const wasDiscounted =
                                  fp.unitPrice != null &&
                                  fp.discountedUnitPrice != null &&
                                  fp.discountedUnitPrice < fp.unitPrice;
                                return (
                                  <>
                                    {" · "}
                                    {wasDiscounted && (
                                      <s className="me-1">{(fp.unitPrice! * fp.foodQuantity).toFixed(2)}{cur}</s>
                                    )}
                                    {(unit * fp.foodQuantity).toFixed(2)} {cur}
                                  </>
                                );
                              })()}
                            </div>
                            {fp.foodOrderNote && (
                              <div className="text-muted fst-italic" style={{ fontSize: "0.75rem" }}>
                                <i className="bi bi-chat-left-text me-1"></i>
                                {fp.foodOrderNote}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      {order.isFeedbackAvailable && (
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm rounded-pill"
                          onClick={() => handleOpenFeedback(order)}
                        >
                          <i className="bi bi-star me-1"></i>
                          {t("feedback.title")}
                        </button>
                      )}
                      {/* Siparişi sohbete bağlar — asistan durum ve teslimat süresini
                          ancak bağlı sipariş varken yanıtlayabiliyor. */}
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-pill"
                        onClick={() => openChat({ orderId: order.id, topic: "OrderIssue" })}
                      >
                        <i className="bi bi-headset me-1"></i>
                        {t("liveChat.helpWithOrder")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OrderHistory;
